import React, { useRef, useState } from 'react';
import { Upload, FileCheck, AlertCircle } from 'lucide-react';
import trainingApi from '../api';
import { labelFor } from '../i18n/strings';

const MAX_SIZE = 200 * 1024 * 1024;

/**
 * Uploads the deck for ONE language. Each language has its own PowerPoint and its own
 * slide images; uploading here replaces only this language's deck.
 */
const PptxUploadBox = ({ trainingId, currentSlideCount, onUploaded, language, sourceFilename }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const validate = (f) => {
    if (!f) return 'No file selected.';
    if (f.size > MAX_SIZE) return 'File too large (max 200 MB).';
    if (!/\.pptx?$/i.test(f.name)) return 'Must be a .ppt or .pptx file.';
    return '';
  };

  const handleSelect = (f) => {
    const err = validate(f);
    if (err) { setError(err); setFile(null); return; }
    setError('');
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    setError('');
    setProgress(0);
    try {
      const res = await trainingApi.adminUploadPptx(trainingId, file, setProgress, language);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      if (onUploaded) onUploaded(res.slide_count, res.language);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800 p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold">
          Slides{language ? ` · ${labelFor(language)}` : ''}
        </h3>
        <span className="text-xs text-slate-400">
          {currentSlideCount > 0 ? `${currentSlideCount} slides loaded` : 'No slides yet'}
          {sourceFilename ? ` · ${sourceFilename}` : ''}
        </span>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => {
          e.preventDefault();
          if (e.dataTransfer.files[0]) handleSelect(e.dataTransfer.files[0]);
        }}
        onClick={() => inputRef.current && inputRef.current.click()}
        className="cursor-pointer border-2 border-dashed border-slate-600 rounded-lg p-6 text-center hover:border-amber-500 transition-colors"
      >
        <input
          ref={inputRef}
          type="file"
          accept=".ppt,.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
          className="hidden"
          onChange={(e) => e.target.files[0] && handleSelect(e.target.files[0])}
        />
        {file ? (
          <div className="flex items-center justify-center gap-2 text-slate-200">
            <FileCheck className="w-5 h-5 text-emerald-400" />
            <span>{file.name}</span>
            <span className="text-xs text-slate-400">
              ({(file.size / 1024 / 1024).toFixed(1)} MB)
            </span>
          </div>
        ) : (
          <div className="text-slate-400">
            <Upload className="w-8 h-8 mx-auto mb-2 text-slate-500" />
            <div>Drop a .pptx here, or click to browse</div>
            <div className="text-xs mt-1">Max 200 MB</div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-3 p-2 rounded bg-rose-900/40 border border-rose-700 text-rose-200 text-sm flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {uploading && (
        <div className="mt-3">
          <div className="text-xs text-slate-300 mb-1">
            {progress < 100 ? `Uploading… ${progress}%` : 'Converting slides on server (this may take a minute)…'}
          </div>
          <div className="w-full h-2 bg-slate-700 rounded overflow-hidden">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={handleUpload}
          disabled={!file || uploading}
          className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-50"
        >
          {uploading ? 'Working…' : 'Upload & Convert'}
        </button>
      </div>
    </div>
  );
};

export default PptxUploadBox;
